import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shell } from '../../components/layout/Shell';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Sparkle, CheckCircle, Pencil, Trash, X,
} from '@phosphor-icons/react';

const QUESTIONS = [
  {
    key: 'q1',
    eyebrow: 'QUESTION 1 OF 5',
    title: "What's the most useful thing you've done in the last 5 years?",
    hint:
      "Paid or unpaid. A business you ran. A job. Raising kids. Volunteering. A side build. " +
      "Don't phrase it cleverly — just say what you did.",
    placeholder:
      'e.g. "Ran my husband\'s plumbing business while he recovered from surgery for 6 months. We didn\'t lose any clients."',
  },
  {
    key: 'q2',
    eyebrow: 'QUESTION 2 OF 5',
    title: 'What did people compliment you on while you were doing it?',
    hint:
      "Family. Customers. Coworkers. Teachers. Anyone. " +
      "If you can't think of compliments, what did people keep coming back to you for?",
    placeholder:
      'e.g. "Apparently I\'m really calm on the phone with upset customers. People also said I always followed up when I said I would."',
  },
  {
    key: 'q3',
    eyebrow: 'QUESTION 3 OF 5',
    title: 'What did you learn doing it?',
    hint: 'Tools, processes, habits, how to handle situations. List anything that comes to mind.',
    placeholder:
      'e.g. "QuickBooks. How to schedule plumbers. How to handle bad reviews on Google. How to talk down an angry homeowner."',
  },
  {
    key: 'q4',
    eyebrow: 'QUESTION 4 OF 5',
    title: "What would you want to do next, if money wasn't a factor?",
    hint: "Don't filter for what's realistic. Just say what you'd pick.",
    placeholder:
      'e.g. "Probably ops manager for a small services company. Or running my own thing again."',
  },
  {
    key: 'q5',
    eyebrow: 'QUESTION 5 OF 5',
    title: 'Anything you\'re proud of from school, hobbies or volunteer work?',
    hint:
      'Coursework. Clubs. A YouTube channel. A treasurer role at the PTA. A wedding you planned. Anything.',
    placeholder:
      'e.g. "Volunteered as treasurer for my kid\'s school PTA for 3 years. Doubled the budget by getting local sponsors."',
  },
];

const SLUG_TO_LABEL = {}; // populated as we receive role_families from the API

const CandidateIntake = () => {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({ q1: '', q2: '', q3: '', q4: '', q5: '' });
  const [phase, setPhase] = useState('questions'); // questions | generating | preview | committing
  const [draft, setDraft] = useState(null);
  const [roleFamilies, setRoleFamilies] = useState([]);
  const [acceptedSlugs, setAcceptedSlugs] = useState(new Set());
  const [headline, setHeadline] = useState('');
  const [projects, setProjects] = useState([]);

  const current = QUESTIONS[idx];
  const value = answers[current.key];

  const onChange = (v) => setAnswers({ ...answers, [current.key]: v });

  const next = () => {
    if (idx < QUESTIONS.length - 1) {
      setIdx(idx + 1);
    } else {
      generate();
    }
  };

  const generate = async () => {
    setPhase('generating');
    try {
      const { data } = await api.post('/candidates/intake/generate', answers);
      const d = data.draft || {};
      setDraft(d);
      setHeadline(d.headline || '');
      setProjects(
        (d.projects || []).map((p) => ({
          ...p,
          skillsText: (p.skills || []).join(', '),
        })),
      );
      setRoleFamilies(data.role_families || []);
      setAcceptedSlugs(new Set((data.role_families || []).map((r) => r.slug)));
      setPhase('preview');
    } catch (e) {
      console.error(e);
      toast.error('Helper is offline. You can still fill the profile manually.');
      setPhase('questions');
    }
  };

  const toggleSlug = (slug) => {
    const next = new Set(acceptedSlugs);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    setAcceptedSlugs(next);
  };

  const updateProject = (i, key, val) => {
    setProjects((ps) => ps.map((p, j) => (j === i ? { ...p, [key]: val } : p)));
  };
  const removeProject = (i) => setProjects((ps) => ps.filter((_, j) => j !== i));

  const commit = async () => {
    setPhase('committing');
    try {
      const payload = {
        headline,
        projects: projects.map((p) => ({
          title: p.title,
          description: p.description,
          skills: (p.skillsText || '')
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean),
        })),
        accepted_role_family_slugs: Array.from(acceptedSlugs),
      };
      await api.post('/candidates/intake/commit', payload);
      toast.success('Profile built — you can fine-tune anytime.');
      navigate('/candidate/profile');
    } catch (e) {
      console.error(e);
      toast.error('Could not save. Try again.');
      setPhase('preview');
    }
  };

  return (
    <Shell>
      <div className="max-w-[760px] mx-auto px-6 py-10" data-testid="candidate-intake">
        <div className="label-eyebrow text-primary mb-2 flex items-center gap-2">
          <Sparkle size={14} weight="fill" /> HIKARI INTAKE
        </div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-tight mb-3">
          Talk about your work.
        </h1>
        <p className="text-sm text-muted-foreground mb-10 leading-relaxed">
          Five short questions. No clever phrasing required — just talk. We'll turn your answers into a
          real Hikari profile you can edit before saving.
        </p>

        {/* --------- QUESTIONS PHASE --------- */}
        {phase === 'questions' && (
          <div className="bg-white border border-border p-8" data-testid="intake-questions">
            <div className="label-eyebrow text-primary mb-2">{current.eyebrow}</div>
            <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tighter leading-tight mb-3">
              {current.title}
            </h2>
            <p className="mono text-xs text-muted-foreground leading-relaxed mb-4">{current.hint}</p>
            <Textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={current.placeholder}
              className="rounded-none min-h-[160px]"
              data-testid={`intake-input-${current.key}`}
              autoFocus
            />

            {/* Progress bar */}
            <div className="mt-6 flex items-center gap-1" data-testid="intake-progress">
              {QUESTIONS.map((q, i) => (
                <div
                  key={q.key}
                  className={`flex-1 h-1 ${
                    i === idx ? 'bg-primary' : i < idx ? 'bg-foreground' : 'bg-secondary'
                  }`}
                />
              ))}
            </div>

            {/* Footer */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => setIdx(Math.max(0, idx - 1))}
                disabled={idx === 0}
                data-testid="intake-back"
              >
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <div className="flex items-center gap-2">
                <button
                  className="label-eyebrow text-muted-foreground hover:text-foreground"
                  onClick={next}
                  data-testid="intake-skip"
                >
                  Skip this one
                </button>
                <Button onClick={next} className="rounded-none" data-testid="intake-next">
                  {idx === QUESTIONS.length - 1 ? 'Build my profile' : 'Next'}
                  <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* --------- GENERATING PHASE --------- */}
        {phase === 'generating' && (
          <div className="bg-white border border-border p-12 text-center" data-testid="intake-generating">
            <Sparkle size={48} weight="fill" className="text-primary mx-auto mb-4 animate-pulse" />
            <div className="font-display font-black text-2xl tracking-tighter mb-2">
              Reading what you wrote…
            </div>
            <p className="mono text-xs text-muted-foreground">
              Translating your answers into a profile. ~10 seconds.
            </p>
          </div>
        )}

        {/* --------- PREVIEW PHASE --------- */}
        {phase === 'preview' && draft && (
          <div className="space-y-6" data-testid="intake-preview">
            {/* Reasoning */}
            {draft.reasoning && (
              <div className="bg-foreground text-background p-6">
                <div className="label-eyebrow text-accent mb-2 flex items-center gap-2">
                  <Sparkle size={12} weight="fill" /> WHY THIS FITS YOU
                </div>
                <p className="text-sm leading-relaxed" data-testid="intake-reasoning">
                  {draft.reasoning}
                </p>
              </div>
            )}

            {/* Headline */}
            <div className="bg-white border border-border p-6">
              <div className="label-eyebrow text-muted-foreground mb-2">YOUR HEADLINE</div>
              <Input
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                className="rounded-none h-12 text-base"
                data-testid="intake-headline"
                placeholder="A short, honest one-liner"
              />
              <p className="mono text-[11px] text-muted-foreground mt-2">
                Edit freely — this is the first thing employers see.
              </p>
            </div>

            {/* Projects */}
            <div className="bg-white border border-border p-6" data-testid="intake-projects">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow text-muted-foreground">PROOF · {projects.length}</div>
                <div className="mono text-[11px] text-muted-foreground">
                  Each box = one piece of proof. Edit, trim, or delete.
                </div>
              </div>
              {projects.length === 0 && (
                <div className="mono text-xs text-muted-foreground py-4 text-center">
                  No projects detected. You can add them on the next screen.
                </div>
              )}
              <div className="space-y-4">
                {projects.map((p, i) => (
                  <div
                    key={i}
                    className="border border-border p-4 relative"
                    data-testid={`intake-project-${i}`}
                  >
                    <button
                      onClick={() => removeProject(i)}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
                      aria-label="Remove project"
                      data-testid={`intake-project-remove-${i}`}
                    >
                      <Trash size={14} />
                    </button>
                    <Input
                      value={p.title}
                      onChange={(e) => updateProject(i, 'title', e.target.value)}
                      placeholder="Title"
                      className="rounded-none h-10 font-semibold"
                    />
                    <Textarea
                      value={p.description}
                      onChange={(e) => updateProject(i, 'description', e.target.value)}
                      placeholder="Description"
                      className="rounded-none mt-2 min-h-[80px]"
                    />
                    <Input
                      value={p.skillsText}
                      onChange={(e) => updateProject(i, 'skillsText', e.target.value)}
                      placeholder="skills, comma_separated"
                      className="rounded-none h-10 mt-2 mono text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Role families */}
            {roleFamilies.length > 0 && (
              <div className="bg-white border border-border p-6" data-testid="intake-role-families">
                <div className="label-eyebrow text-muted-foreground mb-3">
                  SUGGESTED ROLE FAMILIES
                </div>
                <p className="mono text-[11px] text-muted-foreground mb-3">
                  Pick the ones you'd actually consider. We'll use these to personalize your jobs and
                  assessments.
                </p>
                <div className="flex flex-wrap gap-2">
                  {roleFamilies.map((rf) => {
                    const on = acceptedSlugs.has(rf.slug);
                    return (
                      <button
                        key={rf.slug}
                        onClick={() => toggleSlug(rf.slug)}
                        className={`label-eyebrow px-3 py-2 border ${
                          on
                            ? 'bg-foreground text-background border-foreground'
                            : 'bg-white text-foreground border-border hover:border-foreground'
                        }`}
                        data-testid={`intake-rf-${rf.slug}`}
                      >
                        {on && <CheckCircle size={11} weight="fill" className="inline mr-1" />}
                        {rf.name}
                        {rf.sector && (
                          <span className="ml-1 opacity-60">· {rf.sector}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Commit footer */}
            <div className="flex items-center justify-between gap-3 pt-2">
              <Button
                variant="outline"
                className="rounded-none"
                onClick={() => setPhase('questions')}
                data-testid="intake-edit-answers"
              >
                <Pencil size={14} className="mr-1" /> Edit my answers
              </Button>
              <Button
                onClick={commit}
                className="rounded-none"
                data-testid="intake-commit"
              >
                Looks right — build my profile <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* --------- COMMITTING PHASE --------- */}
        {phase === 'committing' && (
          <div className="bg-white border border-border p-12 text-center" data-testid="intake-committing">
            <CheckCircle size={48} weight="fill" className="text-primary mx-auto mb-4 animate-pulse" />
            <div className="font-display font-black text-2xl tracking-tighter mb-2">
              Saving your profile…
            </div>
          </div>
        )}

        {/* Always-available exit */}
        <div className="mt-10 text-center">
          <button
            onClick={() => {
              try {
                sessionStorage.setItem('hikari_helper_autoprompt', 'Help me fill out my profile manually — what should I add first?');
              } catch { /* sessionStorage blocked — silently skip */ }
              navigate('/candidate/profile');
            }}
            className="label-eyebrow text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="intake-exit"
          >
            <X size={11} /> Skip the questions — I'd rather fill it manually
          </button>
          <p className="mono text-[11px] text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
            You'll land on the profile form. The Hikari Helper will be waiting to walk you through it — just ask.
          </p>
        </div>
      </div>
    </Shell>
  );
};

export default CandidateIntake;
